import { useMemo } from 'react';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel, { StepLabelProps } from '@material-ui/core/StepLabel';
import StepConnector from '@material-ui/core/StepConnector';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { themeColor } from '@hilma/netina2all';

import { useActiveStep, useActiveVisibleStep, useFilteredSteps, useNubmeredSteps, usePurchaseLoading, useSetActiveStep } from '../hooks';

import StepIcon from './StepIcon';

const CustomizedStepper: React.FC = () => {
	const activeStep = useActiveVisibleStep();
	const filteredSteps = useFilteredSteps();
	const numberedSteps = useNubmeredSteps();
	const setActiveStep = useSetActiveStep();
	const currentStep = useActiveStep();
	const purchaseLoading = usePurchaseLoading();

	const classes = useStyles();

	const isMobile = useMediaQuery('(max-width: 800px)');

	const disabled = useMemo(() => purchaseLoading || filteredSteps.length - 1 === currentStep || (currentStep === 1), [filteredSteps.length, currentStep, purchaseLoading]);

	const handleStep = (step: number) => () => {
		if (disabled) return;
		setActiveStep(step);
	};

	return (
		<div className='stepper'>
			<Stepper
				className={classes.root}
				activeStep={activeStep}
				connector={(
					<StepConnector
						classes={{
							active: classes.active,
							completed: classes.completed,
							line: classes.line
						}}
					/>
				)}
				orientation={isMobile ? "horizontal" : "vertical"}
			>
				{numberedSteps.map(step => {
					const stepLabelProps: StepLabelProps = {};
					if (step.visited) {
						stepLabelProps.className = clsx(!disabled && 'pointer', classes.greenStep);
						stepLabelProps.StepIconComponent = (props: any) => {
							return (
								<StepIcon
									icon={props.icon}
									clickable={disabled}
									active={props.active}
									visited={step.visited}
									onClick={handleStep(step.index)}
								/>
							);
						}
						stepLabelProps.onClick = handleStep(step.index);
					} else {
						stepLabelProps.className = classes.stepDisabled
						stepLabelProps.StepIconComponent = StepIcon
					}
					return (
						<Step key={step.index}>
							<StepLabel {...stepLabelProps}>
								{isMobile ? <></> : step.label}
							</StepLabel> :
						</Step>
					)
				})}
			</Stepper>
		</div>
	);
}

export default CustomizedStepper;

const useStyles = makeStyles((theme) => ({
	active: {
		'& $line': {
			borderColor: themeColor,
		},
	},

	completed: {
		'& $line': {
			borderColor: themeColor,
		},
	},

	line: {
		padding: 0,
		borderColor: '#7070703b',
		borderTopWidth: 2,
		borderRadius: 0,
	},

	root: {
		padding: '0',

		'& .MuiStepButton-root': {
			padding: 0,
		},

		'& .MuiStepLabel-label': {
			paddingLeft: '1.5rem',
			textAlign: 'left',
			fontFamily: 'Assistant-Regular',
			fontSize: '1rem',
			[theme.breakpoints.up(1600)]: {
				paddingLeft: '2rem',
				fontSize: '1.35rem',
				lineHeight: 1
			},
			'&.MuiStepLabel-active': {
				fontWeight: 600
			}
		},

		'& .MuiStepConnector-vertical': {
			padding: 0,
			marginLeft: '0.8rem',
			[theme.breakpoints.up(1600)]: {
				marginLeft: '1rem'
			},
		},

		'& .MuiStepConnector-lineVertical': {
			minHeight: 15,
			borderLeftWidth: 1.5,
			[theme.breakpoints.up(1600)]: {
				minHeight: 25,
				borderLeftWidth: 2,
			}
		},

		'& .MuiStepLabel-iconContainer': {
			padding: 0
		}

		, '& .MuiStep-horizontal': {
			padding: 0
		},

		'& .MuiStepLabel-root': {
			[theme.breakpoints.down(800)]: {
				display: 'unset'
			}
		}
	},

	stepDisabled: {
		'& .MuiStepLabel-label': {
			color: '#7070703b'
		}
	},

	greenStep: {
		'& .MuiStepLabel-label': {
			color: themeColor
		}
	}
}));